import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import TaskRows, { emptyTask, tasksToSend } from '../components/TaskRows';
import { useTracker } from '../context/TrackerContext';
import {
  taskAnswerDoubt, taskFileLink, taskFileUrl, taskGive, taskList, taskReview, taskUpdate,
} from '../lib/api';
import type { StaffAuth, StudentTaskRow, TaskItem, TaskListData, TaskView } from '../lib/api';

// ── Tasks & Reviews ──────────────────────────────────────────
// Mentors review their students' tasks here (a senior mentor their juniors'
// too; Student Coordinators and the admin everyone's): a score out of 10,
// what's good, what to improve, Accepted or Redo, within 48 hours (Sundays
// and holidays don't count). The tasks come from the class log, or Give a
// task. Mirrors the app's mentor_task_screens.dart; server: routes/tasks.js.

const IST = 'Asia/Kolkata';
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '');
const ago = (mins: number) => (mins < 60 ? `${mins} min` : mins < 48 * 60 ? `${Math.floor(mins / 60)} h` : `${Math.floor(mins / 1440)} days`);
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });

const STATUS_CHIP: Record<string, string> = {
  Given: 'bg-sky-100 text-sky-800', Submitted: 'bg-purple-100 text-purple-800', Accepted: 'bg-green-100 text-green-800',
  Redo: 'bg-amber-100 text-amber-800', Cancelled: 'bg-gray-100 text-gray-500',
};
const STATUS_TEXT: Record<string, string> = { Given: 'To send', Submitted: 'Sent: to review', Accepted: 'Accepted', Redo: 'Redo', Cancelled: 'Cancelled' };

// Who is asking: a mentor, a Student Coordinator ('COORD:' → coordinatorId) or the admin
function useStaff(): { auth: StaffAuth; kind: 'mentor' | 'coordinator' | 'admin'; isSenior: boolean } {
  const { session } = useTracker();
  if (session?.kind === 'mentor') return { auth: { mentorId: session.mentorId }, kind: 'mentor', isSenior: session.role === 'Senior' };
  if (session?.kind === 'coordinator') return { auth: { adminPassword: `COORD:${session.mentorId}` }, kind: 'coordinator', isSenior: false };
  if (session?.kind === 'admin') return { auth: { adminPassword: session.adminPassword }, kind: 'admin', isSenior: false };
  throw new Error('Sign in as a mentor, coordinator or the admin');
}

const Modal: React.FC<{ title: string; sub?: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, sub, onClose, children, wide }) => (
  <div className="fixed inset-0 z-[400] flex items-center justify-center p-3" role="dialog" aria-modal="true" aria-label={title}>
    <div className="absolute inset-0 bg-black/60" onClick={onClose} />
    <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[94vh] flex flex-col overflow-hidden`}>
      <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
        <div className="min-w-0">
          <p className="text-white font-bold truncate">{title}</p>
          {sub && <p className="text-white/60 text-xs mt-0.5">{sub}</p>}
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
      </div>
      <div className="overflow-y-auto p-5">{children}</div>
    </div>
  </div>
);

// What the student sent, try by try (newest first), with the reviews
export const TaskHistory: React.FC<{ auth: StaffAuth; task: StudentTaskRow }> = ({ auth, task: t }) => {
  const open = async (path: string) => {
    try { window.open(taskFileUrl((await taskFileLink(auth, t.id, path)).url), '_blank', 'noopener'); } catch (e: any) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3">
      {[...t.tries].reverse().map(x => {
        const review = t.reviews.find(r => r.n === x.n);
        return (
          <div key={x.n} className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 flex items-center gap-2 flex-wrap text-xs">
              <span className="font-bold text-gray-700">Try {x.n}</span>
              <span className="text-gray-500">sent {when(x.at)}</span>
              {x.late && <span className="font-bold text-red-600">Late (after the due date)</span>}
            </div>
            <div className="p-3 space-y-2 text-sm">
              {x.link && <a href={x.link} target="_blank" rel="noreferrer" className="block text-purple-700 underline break-all">🔗 {x.link}</a>}
              {x.files.map(f => (
                <button key={f.path} onClick={() => open(f.path)} className="block text-left text-purple-700 underline">📎 {f.name} <span className="text-gray-400 text-xs no-underline">({Math.max(1, Math.round(f.size / 1024))} KB)</span></button>
              ))}
              {x.note && <p className="text-gray-600 text-xs bg-gray-50 rounded-lg px-3 py-2">“{x.note}”</p>}
              {review && (
                <div className={`rounded-lg px-3 py-2 ${review.result === 'Accepted' ? 'bg-green-50' : 'bg-amber-50'}`}>
                  <p className="text-xs font-bold text-gray-700">{review.score}/10 · {review.result} · {review.byName}, {when(review.at)}{review.late ? ' · reviewed late' : ''}{review.helpful ? ` · student: ${review.helpful === 'yes' ? '👍 helpful' : '👎 not helpful'}` : ''}</p>
                  <p className="text-xs text-gray-700 mt-1"><b>Good:</b> {review.good}</p>
                  <p className="text-xs text-gray-700"><b>Improve:</b> {review.improve}</p>
                  {(review.checks || []).map((c, i) => (
                    <p key={i} className={`text-[11px] mt-1 font-semibold ${c.verdict === 'good' ? 'text-green-700' : 'text-red-700'}`}>{c.verdict === 'good' ? '👍' : '👎'} {c.byName}: {c.verdict === 'good' ? 'good review' : 'give better feedback'}{c.note ? ` — ${c.note}` : ''}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!t.tries.length && <p className="text-xs text-gray-400">Nothing sent yet.</p>}
    </div>
  );
};

// The student's doubts and the answers; staff can answer
const Doubts: React.FC<{ auth: StaffAuth; task: StudentTaskRow; onChanged: (t: StudentTaskRow) => void }> = ({ auth, task: t, onChanged }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  if (!t.doubts.length) return null;
  const answer = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { onChanged((await taskAnswerDoubt(auth, t.id, text.trim())).task); setText(''); toast.success('Answered. The student gets a notification.'); }
    catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Doubts</p>
      {t.doubts.map((d, i) => (
        <div key={i} className={`text-sm rounded-xl px-3 py-2 ${d.from === 'student' ? 'bg-sky-50 text-sky-900 mr-8' : 'bg-purple-50 text-purple-900 ml-8'}`}>
          <p className="text-[10px] font-bold opacity-70">{d.byName} · {when(d.at)}</p>
          <p>{d.text}</p>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Answer the doubt…" className="input-base" aria-label="Answer" />
        <button onClick={answer} disabled={busy || !text.trim()} className="px-4 rounded-lg text-xs font-bold text-white disabled:opacity-50" style={{ background: '#2b2240' }}>Send</button>
      </div>
    </div>
  );
};

// One task: what was asked, everything sent, and the review form when it waits for one
const TaskModal: React.FC<{ auth: StaffAuth; task: StudentTaskRow; onClose: () => void; onChanged: () => void }> = ({ auth, task: t0, onClose, onChanged }) => {
  const [t, setT] = useState(t0);
  const [score, setScore] = useState<number | null>(null);
  const [good, setGood] = useState('');
  const [improve, setImprove] = useState('');
  const [result, setResult] = useState<'Accepted' | 'Redo' | ''>('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!score) { toast.error('Give a score from 1 to 10'); return; }
    if (!good.trim()) { toast.error("Write what's good"); return; }
    if (!improve.trim()) { toast.error('Write what to improve'); return; }
    if (!result) { toast.error('Pick Accepted or Redo'); return; }
    setBusy(true);
    try {
      await taskReview(auth, t.id, { score, good: good.trim(), improve: improve.trim(), result });
      toast.success(`Sent to ${t.studentName}: ${score}/10, ${result === 'Accepted' ? 'accepted' : 'redo'}`);
      onChanged();
      onClose();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  const changed = (n: StudentTaskRow) => { setT(n); onChanged(); };
  return (
    <Modal title={`${t.studentName} · ${t.title}`} sub={`Due ${t.dueLabel} · given by ${t.givenByName}${t.mentorName ? ` · mentor ${t.mentorName}` : ''}`} onClose={onClose} wide>
      <div className="space-y-4">
        {t.details && <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2 whitespace-pre-wrap">{t.details}</p>}
        <TaskHistory auth={auth} task={t} />
        <Doubts auth={auth} task={t} onChanged={changed} />
        {t.status === 'Submitted' && (
          <div className="rounded-2xl border-2 border-purple-200 p-4 space-y-3">
            <p className="font-bold text-gray-800">Your review {t.reviewLate && <span className="text-xs font-bold text-red-600">· late: over 48 hours</span>}</p>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5">Score</p>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Score">
                {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                  <button key={n} role="radio" aria-checked={score === n} onClick={() => setScore(n)}
                    className={`w-9 h-9 rounded-lg text-sm font-bold border-2 ${score === n ? 'border-purple-600 bg-purple-600 text-white' : n <= 4 ? 'border-red-100 text-red-700' : n <= 6 ? 'border-amber-100 text-amber-700' : 'border-green-100 text-green-700'}`}>{n}</button>
                ))}
              </div>
            </div>
            <label className="block"><span className="text-xs font-semibold text-gray-500">👍 What's good</span>
              <textarea value={good} onChange={e => setGood(e.target.value)} rows={2} maxLength={1500} className="input-base resize-none mt-1" placeholder="e.g. Correct joins, clear column names" />
            </label>
            <label className="block"><span className="text-xs font-semibold text-gray-500">🔧 What to improve</span>
              <textarea value={improve} onChange={e => setImprove(e.target.value)} rows={3} maxLength={1500} className="input-base resize-none mt-1" placeholder="Say exactly what to change, e.g. Use LEFT JOIN so customers with no orders stay in the list" />
            </label>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Result">
              {(['Accepted', 'Redo'] as const).map(r => (
                <button key={r} role="radio" aria-checked={result === r} onClick={() => setResult(r)}
                  className={`py-2.5 rounded-xl text-sm font-bold border-2 ${result === r ? (r === 'Accepted' ? 'border-green-600 bg-green-600 text-white' : 'border-amber-500 bg-amber-500 text-white') : 'border-gray-200 text-gray-600'}`}>
                  {r === 'Accepted' ? '✅ Accepted' : '🔁 Redo'}
                </button>
              ))}
            </div>
            <button onClick={send} disabled={busy} className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
              {busy ? 'Sending…' : 'Send feedback'}
            </button>
            <p className="text-[11px] text-gray-400">The student gets it in the app and by email. Redo: they fix it and send it again.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Change a task nobody has sent yet (or cancel it)
const EditModal: React.FC<{ auth: StaffAuth; task: StudentTaskRow; onClose: () => void; onChanged: () => void }> = ({ auth, task: t, onClose, onChanged }) => {
  const [item, setItem] = useState<TaskItem>({ title: t.title, details: t.details, dueDate: t.dueDate });
  const [busy, setBusy] = useState(false);
  const save = async (cancel = false) => {
    if (cancel && !window.confirm(`Cancel "${t.title}" for ${t.studentName}?`)) return;
    setBusy(true);
    try {
      await taskUpdate(auth, t.id, cancel ? { cancel: true } : { title: item.title.trim(), details: item.details || '', dueDate: item.dueDate });
      toast.success(cancel ? 'Cancelled' : 'Saved');
      onChanged();
      onClose();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${t.studentName} · change the task`} sub="Only before the student sends it" onClose={onClose}>
      <div className="space-y-3">
        <input value={item.title} onChange={e => setItem({ ...item, title: e.target.value })} maxLength={120} className="input-base" aria-label="Title" />
        <textarea value={item.details || ''} onChange={e => setItem({ ...item, details: e.target.value })} rows={3} maxLength={1500} className="input-base resize-none" aria-label="Details" />
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500">Due
          <input type="date" value={item.dueDate || ''} onChange={e => setItem({ ...item, dueDate: e.target.value })} className="input-base max-w-[170px]" />
        </label>
        {t.status === 'Redo' && <p className="text-[11px] text-amber-700">This one is a Redo: the student sends it again by the new due date.</p>}
        <div className="flex gap-2">
          <button onClick={() => save(false)} disabled={busy || !item.title.trim() || !item.dueDate} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: '#2b2240' }}>Save</button>
          {t.status === 'Given' && <button onClick={() => save(true)} disabled={busy} className="px-4 py-2.5 rounded-xl text-sm font-bold border-2 border-red-200 text-red-700">Cancel the task</button>}
        </div>
      </div>
    </Modal>
  );
};

// Give a task outside a class: to some of their students
const GiveModal: React.FC<{ auth: StaffAuth; students: TaskListData['students']; onClose: () => void; onDone: () => void; showMentor: boolean }> = ({ auth, students, onClose, onDone, showMentor }) => {
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<TaskItem[]>([emptyTask()]);
  const [busy, setBusy] = useState(false);
  const shown = students.filter(s => !q || `${s.name} ${s.studentId} ${s.mentorName}`.toLowerCase().includes(q.toLowerCase()));
  const give = async () => {
    const items = tasksToSend(rows);
    if (!picked.length) { toast.error('Pick at least one student'); return; }
    if (!items.length || items.some(i => !i.title)) { toast.error('Give each task a title'); return; }
    setBusy(true);
    try {
      const r = await taskGive(auth, picked, items);
      toast.success(`${r.given} task${r.given === 1 ? '' : 's'} given. The students get a notification.`);
      onDone();
      onClose();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="Give a task" sub="Outside a class. Each student picked gets each task." onClose={onClose} wide>
      <div className="space-y-4">
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students…" className="flex-1 text-xs bg-transparent outline-none" aria-label="Search students" />
            <button type="button" onClick={() => setPicked(picked.length === shown.length ? [] : shown.map(s => s.studentId))} className="text-[10px] font-bold px-2 py-0.5 rounded border border-gray-200 text-gray-500">
              {picked.length === shown.length && shown.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {shown.map(s => (
              <label key={s.studentId} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${picked.includes(s.studentId) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={picked.includes(s.studentId)} onChange={() => setPicked(p => (p.includes(s.studentId) ? p.filter(x => x !== s.studentId) : [...p, s.studentId]))} className="w-4 h-4 accent-purple-600" />
                <span className="text-sm flex-1">{s.name}</span>
                {showMentor && <span className="text-[10px] text-gray-400">{s.mentorName}</span>}
                <span className="text-[10px] text-gray-400 font-mono">{s.studentId}</span>
              </label>
            ))}
            {!shown.length && <p className="text-xs text-gray-400 text-center py-4">No students</p>}
          </div>
        </div>
        <TaskRows rows={rows} onChange={setRows} minDate={today()} />
        <button onClick={give} disabled={busy} className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
          {busy ? 'Giving…' : `Give to ${picked.length || '…'} student${picked.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </Modal>
  );
};

const TaskReviews: React.FC = () => {
  const { auth, kind, isSenior } = useStaff();
  const [view, setView] = useState<TaskView>('review');
  const [scope, setScope] = useState<'mine' | 'team'>('mine');
  const [forMentor, setForMentor] = useState('');
  const [data, setData] = useState<TaskListData | null>(null);
  const [open, setOpen] = useState<StudentTaskRow | null>(null);
  const [edit, setEdit] = useState<StudentTaskRow | null>(null);
  const [giving, setGiving] = useState(false);
  const authKey = JSON.stringify(auth);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableAuth = useMemo(() => auth, [authKey]);

  const load = useCallback(async () => {
    try { setData(await taskList(stableAuth, { view, scope, forMentor })); } catch (e: any) { toast.error(e.message); }
  }, [stableAuth, view, scope, forMentor]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const mentors = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of data?.students || []) if (s.mentorId) m.set(s.mentorId, s.mentorName || s.mentorId);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data?.students]);
  const tabs: { id: TaskView; label: string; n?: number; red?: number }[] = [
    { id: 'review', label: 'To review', n: data?.counts.review, red: data?.counts.late },
    { id: 'waiting', label: 'With students', n: data?.counts.waiting, red: data?.counts.overdue },
    { id: 'done', label: 'Done', n: data?.counts.done },
  ];
  const oldStatus = kind === 'mentor' ? '/tracker/mentor/tasks' : kind === 'coordinator' ? '/tracker/coordinator/tasks' : '';

  return (
    <TrackerLayout>
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>{kind === 'mentor' ? 'Tasks & Reviews' : 'Task Reviews'}</h1>
            <p className="text-gray-500 text-sm mt-0.5">Review each task within 48 hours (Sundays and holidays don't count): a score, what's good, what to improve, Accepted or Redo.</p>
          </div>
          <button onClick={() => setGiving(true)} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>＋ Give a task</button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setView(t.id)} aria-pressed={view === t.id}
              className={`px-4 py-2 rounded-xl text-sm font-bold border-2 flex items-center gap-2 ${view === t.id ? 'border-[#2b2240] bg-[#2b2240] text-white' : 'border-gray-200 text-gray-600 bg-white'}`}>
              {t.label}{t.n !== undefined && <span className="opacity-70">{t.n}</span>}
              {!!t.red && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white">{t.red} {t.id === 'review' ? 'late' : 'overdue'}</span>}
            </button>
          ))}
          {isSenior && (
            <select value={scope} onChange={e => setScope(e.target.value as 'mine' | 'team')} className="ml-auto input-base max-w-[200px]" aria-label="Whose students">
              <option value="mine">My students</option>
              <option value="team">My students + my juniors'</option>
            </select>
          )}
          {data?.seesAll && (
            <select value={forMentor} onChange={e => setForMentor(e.target.value)} className="ml-auto input-base max-w-[220px]" aria-label="Mentor">
              <option value="">All mentors</option>
              {mentors.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
        </div>

        {!data ? <p className="text-gray-400 text-sm py-10 text-center">Loading…</p> : !data.tasks.length ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-3xl">{view === 'review' ? '🎉' : '📭'}</p>
            <p className="font-bold text-gray-700 mt-2">{view === 'review' ? 'Nothing waiting for your review' : view === 'waiting' ? 'No tasks with the students' : 'No accepted tasks yet'}</p>
            <p className="text-xs text-gray-400 mt-1">Type tasks in Log a Class, or press Give a task.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.tasks.map(t => {
              const last = t.tries[t.tries.length - 1];
              return (
                <div key={t.id} className={`bg-white rounded-2xl border p-4 flex items-start gap-3 flex-wrap ${t.reviewLate ? 'border-red-200' : 'border-gray-100'}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800">{t.studentName}</span>
                      {data.seesAll && t.mentorName && <span className="text-[11px] text-gray-400">· {t.mentorName}</span>}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.overdue ? 'bg-red-100 text-red-700' : STATUS_CHIP[t.status]}`}>{t.overdue ? 'Overdue' : STATUS_TEXT[t.status]}</span>
                      {t.reviewLate && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">Late review</span>}
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5">{t.title}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {view === 'review' && last ? `Try ${last.n} · sent ${ago(t.waitingMinutes)} ago${last.late ? ' · after the due date' : ''}`
                        : view === 'done' ? `${t.score}/10 · accepted ${when(t.reviewedAt)}${t.reviews.length > 1 ? ` · ${t.reviews.length} tries` : ''}`
                          : `Due ${t.dueLabel}${t.status === 'Redo' && t.reviews.length ? ` · last score ${t.score}/10` : ''}`}
                      {t.doubts.some(d => d.from === 'student') && t.doubts[t.doubts.length - 1].from === 'student' ? ' · ❓ a doubt to answer' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {view === 'waiting' && <button onClick={() => setEdit(t)} className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">Change</button>}
                    <button onClick={() => setOpen(t)} className="px-4 py-1.5 rounded-lg text-xs font-bold text-white" style={{ background: view === 'review' ? '#7c3aed' : '#2b2240' }}>{view === 'review' ? 'Review' : 'Open'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {oldStatus && (
          <p className="text-[11px] text-gray-400 text-center pt-4">
            Classes logged before tasks were typed in still show on the <Link to={oldStatus} className="underline">old Task Status page</Link>.
          </p>
        )}
      </div>
      {open && <TaskModal auth={stableAuth} task={open} onClose={() => setOpen(null)} onChanged={load} />}
      {edit && <EditModal auth={stableAuth} task={edit} onClose={() => setEdit(null)} onChanged={load} />}
      {giving && data && <GiveModal auth={stableAuth} students={data.students} showMentor={data.seesAll || scope === 'team'} onClose={() => setGiving(false)} onDone={load} />}
      <style>{`
        .input-base { width:100%; padding:0.55rem 0.8rem; border:2px solid #e5e7eb; border-radius:0.6rem; font-size:0.875rem; color:#111827; outline:none; background:#fff; }
        .input-base:focus { border-color:#2b2240; }
      `}</style>
    </TrackerLayout>
  );
};

export default TaskReviews;
